import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import PropertyVisual from '../components/PropertyVisual';
import { useLanguage } from '../context/LanguageContext';
import { getResidence, residenceDisplayOrder } from '../data/residences';
import { getApiUrl, getPublicApiErrorMessage } from '../utils/api';
import { clearBookingSearch, readBookingSearch, saveBookingSearch } from '../utils/bookingSearch';
import { saveBookingSelection } from '../utils/bookingSelection';

const MIN_NIGHTS = 3;
const MAX_NIGHTS = 60;
const MAX_GUESTS = 8;
const AVAILABILITY_ORDER_INDEX = new Map(residenceDisplayOrder.map((residenceId, index) => [residenceId, index]));
const IS_LOCAL_DEMO = process.env.REACT_APP_LOCAL_DEMO_MODE === 'true';
const DEFAULT_BOOKING_OPTIONS = Object.freeze({
  privateParking: false,
  babyCot: false,
  highChair: false,
});

const copyByLanguage = {
  en: {
    eyebrow: 'Live availability',
    title: 'Find an apartment for your dates',
    lead: 'Choose your dates and add the adults and children travelling. We will show the apartments available for your stay.',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    adults: 'Adults',
    children: 'Children',
    childAge: (number) => `Child ${number} age`,
    childAges: 'Children ages',
    selectAge: 'Choose age',
    guests: 'Guests',
    nights: 'Nights',
    check: 'Show available apartments',
    checking: 'Checking availability…',
    availableTitle: 'Available for your dates',
    availableLead: 'Compare the available rates, then choose the apartment and booking terms that suit you.',
    availableCount: 'Apartments available',
    noAvailable: 'No apartments are available for these dates',
    noAvailableLead: 'Try changing the dates or number of guests.',
    totalForStay: 'Total stay',
    average: 'Average per night',
    regularRate: 'Regular rate',
    nonRefundableRate: 'Non-refundable rate',
    nonRefundableTerms: 'No refund after booking',
    cancellationTerms: 'Cancellation terms',
    book: 'Book this rate',
    view: 'View apartment',
    summaryTitle: 'Your search',
    summaryLead: 'Enter dates and guest numbers to check live availability.',
    note: `Minimum stay is ${MIN_NIGHTS} nights. Availability and prices are confirmed by a live search.`,
    dateError: `Choose a stay between ${MIN_NIGHTS} and ${MAX_NIGHTS} nights.`,
    guestError: `A booking can include up to ${MAX_GUESTS} guests in total.`,
    genericError: 'The booking service could not complete the request. Please try again.',
    demoBanner: 'LOCAL DEMO · Availability and prices are generated only on this computer.',
    amenities: 'Requests for your stay',
    amenitiesHint: 'Select the services you would like us to prepare.',
    addOption: 'Add to booking',
    selectedOption: 'Selected',
    noOptions: 'No additional services selected',
    parking: 'Private parking',
    babyCot: 'Baby cot available',
    highChair: 'High chair available',
  },
  ru: {
    eyebrow: 'Актуальная доступность',
    title: 'Найдите апартамент на ваши даты',
    lead: 'Выберите даты и укажите количество взрослых и детей. Мы покажем апартаменты, свободные на выбранный период.',
    checkIn: 'Заезд',
    checkOut: 'Выезд',
    adults: 'Взрослые',
    children: 'Дети',
    childAge: (number) => `Возраст ребёнка ${number}`,
    childAges: 'Возраст детей',
    selectAge: 'Выберите возраст',
    guests: 'Гости',
    nights: 'Ночей',
    check: 'Показать свободные апартаменты',
    checking: 'Проверяем доступность…',
    availableTitle: 'Свободно на ваши даты',
    availableLead: 'Сравните доступные тарифы и выберите подходящие условия бронирования.',
    availableCount: 'Свободных апартаментов',
    noAvailable: 'На эти даты свободных апартаментов нет',
    noAvailableLead: 'Попробуйте изменить даты или количество гостей.',
    totalForStay: 'За всё проживание',
    average: 'В среднем за ночь',
    regularRate: 'Обычный тариф',
    nonRefundableRate: 'Невозвратный тариф',
    nonRefundableTerms: 'Без возврата после бронирования',
    cancellationTerms: 'Условия отмены',
    book: 'Забронировать',
    view: 'Смотреть апартамент',
    summaryTitle: 'Ваш поиск',
    summaryLead: 'Укажите даты и количество гостей, чтобы проверить актуальную доступность.',
    note: `Минимальное проживание — ${MIN_NIGHTS} ночи. Доступность и цены подтверждаются актуальным поиском.`,
    dateError: `Выберите период от ${MIN_NIGHTS} до ${MAX_NIGHTS} ночей.`,
    guestError: `В одном бронировании может быть не более ${MAX_GUESTS} гостей.`,
    genericError: 'Не удалось выполнить запрос. Пожалуйста, попробуйте ещё раз.',
    demoBanner: 'ЛОКАЛЬНЫЙ DEMO-РЕЖИМ · Доступность и цены формируются только на этом компьютере.',
    amenities: 'Пожелания к проживанию',
    amenitiesHint: 'Выберите услуги, которые нужно подготовить к вашему приезду.',
    addOption: 'Добавить к бронированию',
    selectedOption: 'Выбрано',
    noOptions: 'Дополнительные услуги не выбраны',
    parking: 'Частная парковка',
    babyCot: 'Детская кроватка',
    highChair: 'Детский стульчик',
  },
};

function BookingPage() {
  const { language } = useLanguage();
  const copy = copyByLanguage[language];
  const [searchParams] = useSearchParams();
  const preferredResidence = getResidence(searchParams.get('residence'));
  const [restoredSearch] = useState(() => readBookingSearch());
  const [checkIn, setCheckIn] = useState(() => restoredSearch?.checkInDateLocalized || '');
  const [checkOut, setCheckOut] = useState(() => restoredSearch?.checkOutDateLocalized || '');
  const [adultsCount, setAdultsCount] = useState(() => restoredSearch?.adultsCount || 2);
  const [childrenCount, setChildrenCount] = useState(() => restoredSearch?.childrenCount || 0);
  const [childrenAges, setChildrenAges] = useState(() => restoredSearch?.childrenAges || []);
  const [bookingOptions, setBookingOptions] = useState(() => ({
    ...DEFAULT_BOOKING_OPTIONS,
    ...(restoredSearch?.bookingOptions || {}),
  }));
  const [availability, setAvailability] = useState(() => restoredSearch?.availability || null);
  const [searchStatus, setSearchStatus] = useState(() => restoredSearch ? 'success' : 'idle');
  const [searchError, setSearchError] = useState('');

  const guestsCount = adultsCount + childrenCount;
  const nights = useMemo(() => getNights(checkIn, checkOut), [checkIn, checkOut]);
  const dateError = Boolean(checkIn && checkOut && (nights < MIN_NIGHTS || nights > MAX_NIGHTS));
  const guestError = guestsCount > MAX_GUESTS;
  const childrenAgesIncomplete = childrenAges.length !== childrenCount || childrenAges.some((age) => age === '');
  const today = formatDate(new Date());
  const earliestCheckout = checkIn ? addDays(checkIn, MIN_NIGHTS) : today;
  const availableQuotes = useMemo(() => {
    const items = Array.isArray(availability?.residences) ? [...availability.residences] : [];

    items.sort((left, right) => {
      if (preferredResidence) {
        const preferredDifference = Number(right.residenceId === preferredResidence.id) - Number(left.residenceId === preferredResidence.id);
        if (preferredDifference !== 0) return preferredDifference;
      }

      const leftIndex = AVAILABILITY_ORDER_INDEX.get(left.residenceId) ?? Number.MAX_SAFE_INTEGER;
      const rightIndex = AVAILABILITY_ORDER_INDEX.get(right.residenceId) ?? Number.MAX_SAFE_INTEGER;
      return leftIndex - rightIndex;
    });

    return items;
  }, [availability, preferredResidence]);

  useEffect(() => {
    if (searchStatus !== 'success' || window.location.hash !== '#availability') return undefined;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById('availability')?.scrollIntoView({ block: 'start' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [searchStatus]);

  const clearSearchResults = () => {
    clearBookingSearch();
    setAvailability(null);
    setSearchStatus('idle');
    setSearchError('');
  };

  const handleChildrenCountChange = (event) => {
    const nextCount = Number(event.target.value);
    setChildrenCount(nextCount);
    setChildrenAges((currentAges) => Array.from(
      { length: nextCount },
      (_, index) => currentAges[index] ?? '',
    ));
    if (nextCount === 0) {
      setBookingOptions((current) => ({ ...current, babyCot: false, highChair: false }));
    }
    clearSearchResults();
  };

  const handleChildAgeChange = (index, value) => {
    setChildrenAges((currentAges) => currentAges.map((age, ageIndex) => (ageIndex === index ? value : age)));
    clearSearchResults();
  };

  const handleBookingOptionChange = (option, checked) => {
    const nextOptions = { ...bookingOptions, [option]: checked };
    setBookingOptions(nextOptions);

    if (availability) {
      saveBookingSearch({
        checkInDateLocalized: checkIn,
        checkOutDateLocalized: checkOut,
        adultsCount,
        childrenCount,
        childrenAges: childrenAges.map(Number),
        bookingOptions: nextOptions,
        availability,
      });
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    if (!checkIn || !checkOut || dateError || guestError || childrenAgesIncomplete) return;

    setSearchStatus('loading');
    setSearchError('');
    setAvailability(null);

    try {
      const response = await fetch(getApiUrl('/api/guesty/availability'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkInDateLocalized: checkIn,
          checkOutDateLocalized: checkOut,
          adultsCount,
          childrenCount,
          childrenAges: childrenAges.map(Number),
          guestsCount,
        }),
      });
      const data = await readResponse(response);

      if (!response.ok) throw new Error(getPublicApiErrorMessage(data.message, copy.genericError));
      if (!Array.isArray(data.residences)) throw new Error(copy.genericError);

      setAvailability(data);
      setSearchStatus('success');
      saveBookingSearch({
        checkInDateLocalized: checkIn,
        checkOutDateLocalized: checkOut,
        adultsCount,
        childrenCount,
        childrenAges: childrenAges.map(Number),
        bookingOptions,
        availability: data,
      });
    } catch (error) {
      setSearchStatus('error');
      setSearchError(error.message || copy.genericError);
    }
  };

  return (
    <section className="booking-page page-shell">
      {IS_LOCAL_DEMO && <div className="demo-banner">{copy.demoBanner}</div>}
      <header className="page-intro page-intro_left">
        <span className="eyebrow">{copy.eyebrow}</span>
        <h1>{copy.title}</h1>
        <p>{copy.lead}</p>
      </header>

      <div className="booking-layout">
        <form className="booking-form" onSubmit={handleSearch}>
          <div className="booking-form__dates">
            <div className="field">
              <label htmlFor="check-in">{copy.checkIn}</label>
              <input id="check-in" type="date" min={today} value={checkIn} onChange={(event) => { setCheckIn(event.target.value); setCheckOut(''); clearSearchResults(); }} required />
            </div>
            <div className="field">
              <label htmlFor="check-out">{copy.checkOut}</label>
              <input id="check-out" type="date" min={earliestCheckout} value={checkOut} onChange={(event) => { setCheckOut(event.target.value); clearSearchResults(); }} required />
            </div>
          </div>

          <div className="booking-form__guests">
            <div className="field">
              <label htmlFor="adults">{copy.adults}</label>
              <select id="adults" value={adultsCount} onChange={(event) => { setAdultsCount(Number(event.target.value)); clearSearchResults(); }}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((count) => <option key={count} value={count}>{count}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="children">{copy.children}</label>
              <select id="children" value={childrenCount} onChange={handleChildrenCountChange}>
                {[0, 1, 2, 3, 4, 5, 6, 7].map((count) => <option key={count} value={count}>{count}</option>)}
              </select>
            </div>
          </div>

          {childrenCount > 0 && (
            <div className="booking-form__child-ages" role="group" aria-label={copy.childAges}>
              {childrenAges.map((age, index) => (
                <div className="field" key={`child-age-${index + 1}`}>
                  <label htmlFor={`child-age-${index + 1}`}>{copy.childAge(index + 1)}</label>
                  <select
                    id={`child-age-${index + 1}`}
                    value={age}
                    onChange={(event) => handleChildAgeChange(index, event.target.value)}
                    required
                  >
                    <option value="" disabled>{copy.selectAge}</option>
                    {Array.from({ length: 18 }, (_, childAge) => (
                      <option key={childAge} value={childAge}>{childAge}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          <fieldset className="booking-options">
            <legend>{copy.amenities}</legend>
            <p>{copy.amenitiesHint}</p>
            <div className="booking-amenities">
              <BookingOption
                checked={bookingOptions.privateParking}
                icon="P"
                label={copy.parking}
                onChange={(checked) => handleBookingOptionChange('privateParking', checked)}
                status={bookingOptions.privateParking ? copy.selectedOption : copy.addOption}
              />
            {childrenCount > 0 && (
              <>
                  <BookingOption
                    checked={bookingOptions.babyCot}
                    icon="0+"
                    label={copy.babyCot}
                    onChange={(checked) => handleBookingOptionChange('babyCot', checked)}
                    status={bookingOptions.babyCot ? copy.selectedOption : copy.addOption}
                  />
                  <BookingOption
                    checked={bookingOptions.highChair}
                    icon="↥"
                    label={copy.highChair}
                    onChange={(checked) => handleBookingOptionChange('highChair', checked)}
                    status={bookingOptions.highChair ? copy.selectedOption : copy.addOption}
                  />
              </>
            )}
            </div>
          </fieldset>

          {dateError && <p className="form-error">{copy.dateError}</p>}
          {guestError && <p className="form-error">{copy.guestError}</p>}
          {searchError && <p className="form-error" role="alert">{searchError}</p>}

          <button className="button booking-form__submit" type="submit" disabled={!checkIn || !checkOut || dateError || guestError || searchStatus === 'loading'}>
            {searchStatus === 'loading' ? copy.checking : copy.check}
          </button>
          <p className="booking-form__note">{copy.note}</p>

          {searchStatus === 'success' && (
            <section className="availability-results" id="availability" aria-live="polite">
              {availableQuotes.length > 0 ? (
                <>
                  <div className="availability-results__heading">
                    <div>
                      <span className="eyebrow">{availableQuotes.length} · {copy.availableCount}</span>
                      <h2>{copy.availableTitle}</h2>
                      <p>{copy.availableLead}</p>
                    </div>
                  </div>
                  <div className="availability-grid">
                    {availableQuotes.map((availableQuote) => {
                      const residence = getResidence(availableQuote.residenceId);
                      if (!residence) return null;
                      const ratePlans = getAvailableRatePlans(availableQuote);

                      return (
                        <article className="availability-card" key={residence.id}>
                          <PropertyVisual src={residence.image} alt={residence.name[language]} className="availability-card__visual" />
                          <div className="availability-card__content">
                            <span className="eyebrow">{residence.category[language]}</span>
                            <h3>{residence.name[language]}</h3>
                            <p className="availability-card__specs">{residence.specs[language].join(' · ')}</p>
                            <div className="availability-card__rates">
                              {ratePlans.map((ratePlan) => (
                                <section className="rate-option" key={ratePlan.ratePlanId}>
                                  <div className="rate-option__heading">
                                    <strong>{ratePlan.isNonRefundable ? copy.nonRefundableRate : copy.regularRate}</strong>
                                  </div>
                                  <div className="availability-card__price">
                                    <span>{copy.totalForStay} · {nights} {copy.nights.toLowerCase()}</span>
                                    <strong>{formatCurrency(ratePlan.total, ratePlan.currency, language)}</strong>
                                    <small>{copy.average}: {formatCurrency(ratePlan.total / nights, ratePlan.currency, language)}</small>
                                  </div>
                                  {ratePlan.cancellationPolicy?.length > 0 && (
                                    <p className="rate-option__policy"><b>{copy.cancellationTerms}:</b> {formatCancellationPolicy(ratePlan.cancellationPolicy, ratePlan.isNonRefundable, language, copy)}</p>
                                  )}
                                  <Link
                                    className="button button_small rate-option__book"
                                    to="/book/confirm"
                                    onClick={() => saveBookingSelection(createBookingSelection(availableQuote, ratePlan, {
                                      checkIn,
                                      checkOut,
                                      adultsCount,
                                      childrenCount,
                                      childrenAges,
                                      bookingOptions,
                                      guestsCount,
                                      nights,
                                    }))}
                                  >
                                    {copy.book}
                                  </Link>
                                </section>
                              ))}
                            </div>
                            <Link className="text-link" to={`/residences/${residence.id}?return=booking`}>{copy.view}</Link>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="availability-empty">
                  <span className="availability-empty__number">00</span>
                  <h2>{copy.noAvailable}</h2>
                  <p>{copy.noAvailableLead}</p>
                </div>
              )}
            </section>
          )}
        </form>

        <aside className="booking-summary">
          <span className="eyebrow">AMR · Sveti Stefan</span>
          <h2>{copy.summaryTitle}</h2>
          {availability ? (
            <>
              <SummaryRow label={copy.checkIn} value={formatDisplayDate(checkIn, language)} />
              <SummaryRow label={copy.checkOut} value={formatDisplayDate(checkOut, language)} />
              <SummaryRow label={copy.adults} value={adultsCount} />
              <SummaryRow label={copy.children} value={childrenCount} />
              {childrenCount > 0 && <SummaryRow label={copy.childAges} value={childrenAges.join(', ')} />}
              <SummaryRow label={copy.amenities} value={formatBookingOptions(bookingOptions, copy)} />
              <SummaryRow label={copy.nights} value={nights} />
              <div className="booking-summary__total"><span>{copy.availableCount}</span><strong>{availableQuotes.length}</strong></div>
            </>
          ) : (
            <>
              <div className="booking-summary__number">AMR</div>
              <p>{copy.summaryLead}</p>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

function SummaryRow({ label, value }) {
  return <div className="booking-summary__row"><span>{label}</span><strong>{value}</strong></div>;
}

function BookingOption({ checked, icon, label, onChange, status }) {
  return (
    <label className={`booking-amenity ${checked ? 'booking-amenity_selected' : ''}`}>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="booking-amenity__icon" aria-hidden="true">{icon}</span>
      <span className="booking-amenity__copy">
        <strong>{label}</strong>
        <small>{status}</small>
      </span>
    </label>
  );
}

function formatBookingOptions(options, copy) {
  const selected = [
    options.privateParking && copy.parking,
    options.babyCot && copy.babyCot,
    options.highChair && copy.highChair,
  ].filter(Boolean);

  return selected.length > 0 ? selected.join(' · ') : copy.noOptions;
}

function getNights(checkIn, checkOut) {
  if (!checkIn || !checkOut) return 0;
  return Math.max(0, Math.round((Date.parse(checkOut) - Date.parse(checkIn)) / 86_400_000));
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(dateString, days) {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(date.getDate() + days);
  return formatDate(date);
}

function formatDisplayDate(dateString, language) {
  if (!dateString) return '—';
  return new Date(`${dateString}T12:00:00`).toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatCurrency(value, currency = 'EUR', language = 'en') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat(language === 'ru' ? 'ru-RU' : 'en-GB', { style: 'currency', currency, maximumFractionDigits: Number.isInteger(amount) ? 0 : 2 }).format(amount);
}

function getAvailableRatePlans(quote) {
  if (Array.isArray(quote?.ratePlans) && quote.ratePlans.length > 0) return quote.ratePlans;

  if (!quote?.ratePlanId) return [];
  return [{
    ratePlanId: quote.ratePlanId,
    ratePlanName: quote.ratePlanName,
    currency: quote.currency,
    total: quote.total,
    nights: quote.nights,
    cancellationPolicy: [],
    isNonRefundable: false,
  }];
}

function createBookingSelection(quote, ratePlan, stay) {
  return {
    residenceId: quote.residenceId,
    quoteId: quote.quoteId,
    ratePlanId: ratePlan.ratePlanId,
    ratePlanName: ratePlan.ratePlanName,
    isNonRefundable: ratePlan.isNonRefundable,
    cancellationPolicy: ratePlan.cancellationPolicy,
    ratePlans: getAvailableRatePlans(quote).map((availableRatePlan) => ({
      ratePlanId: availableRatePlan.ratePlanId,
      ratePlanName: availableRatePlan.ratePlanName,
      isNonRefundable: availableRatePlan.isNonRefundable,
      cancellationPolicy: availableRatePlan.cancellationPolicy,
      cancellationFee: availableRatePlan.cancellationFee,
      currency: availableRatePlan.currency,
      total: availableRatePlan.total,
    })),
    currency: ratePlan.currency,
    total: ratePlan.total,
    nights: stay.nights,
    checkInDateLocalized: stay.checkIn,
    checkOutDateLocalized: stay.checkOut,
    adultsCount: stay.adultsCount,
    childrenCount: stay.childrenCount,
    childrenAges: stay.childrenAges.map(Number),
    bookingOptions: stay.bookingOptions,
    guestsCount: stay.guestsCount,
    expiresAt: quote.expiresAt,
  };
}

function formatCancellationPolicy(policies, isNonRefundable, language, copy) {
  if (isNonRefundable) return copy.nonRefundableTerms;

  const knownPolicies = language === 'ru'
    ? { firm: '1 ночь при отмене менее чем за 14 дней', moderate: 'Умеренные условия', flexible: 'Гибкие условия', super_strict: 'Максимально строгие условия' }
    : { firm: '1 night charged if cancelled less than 14 days before check-in', moderate: 'Moderate terms', flexible: 'Flexible terms', super_strict: 'Super-strict terms' };

  return policies.map((policy) => {
    const normalized = String(policy).trim().toLowerCase();
    if (knownPolicies[normalized]) return knownPolicies[normalized];
    return String(policy).replaceAll('_', ' ');
  }).join(' · ');
}

async function readResponse(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export default BookingPage;
